import React, { memo, useState } from "react";
import { Typography, useTheme, Link, Box, Tooltip, Collapse } from "@mui/material";
import { Row, Col } from "v2/components/base/Flex/ui";
import { ShillRecord } from "../types";
import { TimeToNow } from "components/TimeToNow";
// Use a more generic approach for the link icon since we might not have @mui/icons-material
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface ShillRecordItemProps {
  record: ShillRecord;
}

/**
 * Component to display an individual shill record with timestamp, message preview, and expandable details
 */
const ShillRecordItem: React.FC<ShillRecordItemProps> = memo(({ record }) => {
  const theme = useTheme();
  const { timestamp, messageId, messageText } = record;
  const [isExpanded, setIsExpanded] = useState(false);

  const fullDateTime = new Date(timestamp * 1000).toLocaleString();

  // Create preview text (truncate if too long)
  const previewText =
    messageText && messageText.length > 100
      ? `${messageText.substring(0, 100)}...`
      : messageText || "No message text";

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Col
      sx={{
        borderRadius: "8px",
        background: theme.palette.surface.bg,
        mb: 1,
        transition: "all 0.2s ease",
        "&:hover": {
          background: theme.palette.surface.silver,
        },
        overflow: "hidden",
      }}
      fullWidth>
      {/* Main row with timestamp, preview, and actions */}
      <Row
        type="bc"
        sx={{
          py: 1.5,
          px: 2,
          cursor: "pointer",
        }}
        fullWidth
        onClick={handleToggleExpand}>
        {/* Left side: Timestamp and message preview */}
        <Col type="ss" sx={{ flex: 1, minWidth: 0 }}>
          {/* Timestamp */}
          <Tooltip title={fullDateTime} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: "monospace",
                letterSpacing: "0.5px",
                mb: 0.5,
              }}>
              <TimeToNow time={timestamp} />
            </Typography>
          </Tooltip>

          {/* Message preview */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}>
            {previewText}
          </Typography>
        </Col>

        {/* Right side: Actions */}
        <Row type="cc" gap="4px">
          {/* Link Icon */}
          {messageId && messageId !== "--" ? (
            <Tooltip title="Open message" arrow placement="left">
              <Link
                href={messageId}
                target="_blank"
                rel="noopener"
                underline="none"
                aria-label={`Message link ${messageId}`}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.text.primary,
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    background: theme.palette.surface.silver,
                  },
                }}>
                <OpenInNewIcon fontSize="small" />
              </Link>
            </Tooltip>
          ) : (
            <Box sx={{ width: 32, height: 32 }} />
          )}

          {/* Expand/Collapse Icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.palette.text.secondary,
              width: 32,
              height: 32,
              borderRadius: "50%",
              transition: "all 0.2s ease",
              "&:hover": {
                color: theme.palette.primary.main,
                background: theme.palette.surface.silver,
              },
            }}>
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
        </Row>
      </Row>

      {/* Expanded content */}
      <Collapse in={isExpanded} sx={{ width: "100%" }}>
        <Box sx={{ px: 2, pb: 2, pt: 0 }}>
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.border.light}`,
              background: theme.palette.surface.silver,
              width: isExpanded ? "100%" : "0%",
              height: "2px",
              marginBottom: "12px",
              marginX: "auto",
              transition: "width 0.2s ease-in-out",
            }}
          />
          <Col type="ss" gap="8px" width="100%">
            {/* Full message */}
            <Box width="100%">
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  background: theme.palette.surface.container,
                  p: 1.5,
                  borderRadius: "6px",
                  border: `1px solid ${theme.palette.border.light}`,
                }}>
                {messageText || "No message text available"}
              </Typography>
            </Box>

            {/* Message details */}
            <Row type="ec" fullWidth>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {fullDateTime}
              </Typography>
            </Row>
          </Col>
        </Box>
      </Collapse>
    </Col>
  );
});

export default ShillRecordItem;
